import { supabase } from '../supabase/config';
import { generateShortId } from '../utils/customer';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../utils/loyalty';
import { recordConsent } from '../utils/consent';

export const checkInService = {
    async fetchTenant(tenantId) {
        try {
            if (!tenantId) return null;

            // Try 1: Original ID as-is (handles prefixed IDs in DB)
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId);
            if (isUUID) {
                const { data: origData } = await supabase
                    .from('tenants')
                    .select('*')
                    .eq('id', tenantId)
                    .maybeSingle();
                if (origData) return { id: origData.id, data: origData };
            }

            // Try 2: Cleaned ID without prefix
            if (tenantId.startsWith('tenant_')) {
                const cleanId = tenantId.replace(/^tenant_/, '');
                const { data: cleanData } = await supabase
                    .from('tenants')
                    .select('*')
                    .eq('id', cleanId)
                    .maybeSingle();
                if (cleanData) return { id: cleanData.id, data: cleanData };
            }

            // Try 3: Slug fallback
            const { data: slugData, error: slugError } = await supabase
                .from('tenants')
                .select('*')
                .eq('slug', tenantId)
                .maybeSingle();

            if (slugError) return { error: slugError.message };
            if (slugData) return { id: slugData.id, data: slugData };

            return null; // Not found at all
        } catch (e) {
            console.warn("fetchTenant error:", e.message);
            try {
                const { data } = await supabase.from('tenants').select('*').eq('slug', tenantId).maybeSingle();
                if (data) return { id: data.id, data };
            } catch {}
            return { error: e.message };
        }
    },

    async fetchMenu(tenantId, activeMenuId) {
        let query = supabase
            .from('menus')
            .select('*')
            .eq('tenant_id', tenantId);

        if (activeMenuId) {
            query = query.eq('id', activeMenuId);
        }

        const { data, error } = await query.order('updated_at', { ascending: false }).limit(1).maybeSingle();

        if (error || !data) return null;

        const mappedData = {
            ...data,
            menuName: data.menu_name,
            categoriesJson: data.categories_json,
            designRecommendation: data.design_recommendation
        };

        if (mappedData.categoriesJson) {
            try {
                mappedData.categories = JSON.parse(mappedData.categoriesJson);
            } catch (e) {
                mappedData.categories = [];
            }
        }
        return mappedData;
    },

    async processCheckIn(tenantId, customerData, tenantData) {
        // Point gain from visit is now DISABLED (0)
        const points = 0;
        const now = new Date().toISOString();

        // Read current customer data
        const { data: currentData, error: fetchError } = await supabase
            .from('customers')
            .select('*')
            .eq('id', customerData.id)
            .single();

        if (fetchError || !currentData) throw new Error('CUSTOMER_NOT_FOUND');

        const currentPoints = currentData.points || 0;
        const currentTotalPoints = currentData.total_points || 0;
        const currentInteractionCount = currentData.interaction_count || 0;
        const currentVisitTimestamps = currentData.visit_timestamps || [];

        const updatedData = {
            ...customerData,
            points: currentPoints + points,
            interactionCount: currentInteractionCount + 1
        };

        const pointEarnings = currentData.point_earnings || [];
        const newVisitTimestamps = [...currentVisitTimestamps, now];

        const updates = {
            points: currentPoints + points,
            total_points: currentTotalPoints + points,
            interaction_count: currentInteractionCount + 1,
            last_interaction_at: now,
            visit_timestamps: newVisitTimestamps,
            loyalty_level: calculateLoyaltyLevel({ ...updatedData, pointEarnings }, tenantData)?.label || null,
            visit_role: calculateVisitFrequencyRole(updatedData, tenantData)?.label || null
        };

        const { error: updateError } = await supabase
            .from('customers')
            .update(updates)
            .eq('id', customerData.id);
        if (updateError) throw updateError;

        // Interaction record is now handled by activity logger or omitted for performance
        /*
        await supabase.from('customer_interactions').insert({
            customer_id: customerData.id,
            tenant_id: tenantId,
            type: 'Scan',
            content: 'QR Scan / Check-in',
            timestamp: now,
            is_auto: true
        });
        */

        return {
            points,
            total: updatedData.points,
            customerId: customerData.id,
            shortId: customerData.shortId
        };
    },

    async registerCustomer(tenantId, formData, tenantData) {
        const rules = tenantData?.loyalty_rules || tenantData?.loyaltyRules || {};
        const totalInitialPoints = rules.welcomePoints || 0;
        const shortId = generateShortId();
        const nowIso = new Date().toISOString();

        const initialCalcData = {
            ...formData,
            tenantId,
            shortId,
            points: totalInitialPoints,
            totalPoints: totalInitialPoints,
            pointEarnings: [],
            interactionCount: 1,
            visitTimestamps: [nowIso],
            loyaltyLevel: tenantData?.loyaltyLevels?.[0]?.label || null,
            visitRole: 'Yeni Uye'
        };

        const insertData = {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email || null,
            phone: formData.phone || null,
            birthday: formData.birthday || null,
            password: formData.password || null,
            tenant_id: tenantId,
            short_id: shortId,
            points: totalInitialPoints,
            total_points: totalInitialPoints,
            point_earnings: [],
            interaction_count: 1,
            created_at: nowIso,
            last_interaction_at: nowIso,
            visit_timestamps: [nowIso],
            loyalty_level: calculateLoyaltyLevel(initialCalcData, tenantData)?.label || initialCalcData.loyaltyLevel,
            visit_role: calculateVisitFrequencyRole(initialCalcData, tenantData)?.label || initialCalcData.visitRole
        };

        const { data, error } = await supabase
            .from('customers')
            .insert(insertData)
            .select()
            .single();

        if (error) throw error;

        // Record KVKK/privacy consent (best-effort; won't break registration)
        await recordConsent(supabase, 'customers', data.id, nowIso);

        // Interaction logging deprecated
        /*
        await supabase.from('customer_interactions').insert({
            customer_id: data.id,
            tenant_id: tenantId,
            type: 'Scan',
            content: 'Initial Registration Scan',
            timestamp: nowIso,
            is_auto: true
        });
        */

        // Interaction logging deprecated
        /*
        if (totalInitialPoints > 0) {
            await supabase.from('customer_interactions').insert({
                customer_id: data.id,
                tenant_id: tenantId,
                type: 'PointAdjustment',
                content: 'Hoş Geldin Hediyesi',
                points: totalInitialPoints,
                timestamp: nowIso,
                is_auto: true
            });
        }
        */

        return { id: data.id, customerId: data.id, shortId, points: totalInitialPoints, total: totalInitialPoints };
    }
};
